
/**
 * Simple in-memory metrics tracking for voice commands.
 * This helps track which commands are succeeding/failing
 * to improve the voice assistant over time.
 */

export type CommandMetric = {
  command: string;
  success: boolean;
  timestamp: number;
};

// Command metrics by command text
export interface CommandMetricsByCommand {
  [command: string]: { 
    success: number; 
    failure: number; 
    lastUsed?: number;
  };
}

// Store metrics with a maximum length
const MAX_METRICS = 100;
let commandMetrics: CommandMetric[] = [];

/**
 * Records the result of a voice command execution
 * @param command - The command text
 * @param success - Whether the command was successfully executed
 */
export function recordCommandResult(command: string, success: boolean) {
  // Limit size of metrics stored
  if (commandMetrics.length >= MAX_METRICS) {
    commandMetrics = commandMetrics.slice(-MAX_METRICS + 1);
  }
  
  commandMetrics.push({
    command,
    success,
    timestamp: Date.now()
  });
}

/**
 * Returns statistics about command success/failure
 */
export function getSummaryMetrics() {
  const total = commandMetrics.length;
  if (total === 0) return { 
    totalCommandsTracked: "0", 
    totalExecutions: "0", 
    successRate: "0%",
    topCommands: [],
    mostFailedCommands: []
  };
  
  const successful = commandMetrics.filter(m => m.success).length;
  const successRate = (successful / total * 100).toFixed(1);
  
  // Get command-specific metrics
  const byCommand = getAllCommandMetrics();
  
  // Generate top commands
  const topCommands = Object.entries(byCommand)
    .sort((a, b) => (b[1].success + b[1].failure) - (a[1].success + a[1].failure))
    .slice(0, 5)
    .map(([command, stats]) => ({
      command,
      uses: (stats.success + stats.failure).toString(),
      successRate: ((stats.success / (stats.success + stats.failure)) * 100).toFixed(1) + '%'
    }));

  // Generate most failed commands
  const mostFailedCommands = Object.entries(byCommand)
    .filter(([_, stats]) => stats.failure > 0)
    .sort((a, b) => b[1].failure - a[1].failure)
    .slice(0, 5)
    .map(([command, stats]) => ({
      command,
      attempts: (stats.success + stats.failure).toString(),
      failureRate: ((stats.failure / (stats.success + stats.failure)) * 100).toFixed(1) + '%'
    }));
  
  return {
    totalCommandsTracked: Object.keys(byCommand).length.toString(),
    totalExecutions: total.toString(),
    successRate: successRate + '%',
    topCommands,
    mostFailedCommands
  };
}

/**
 * Get all command metrics grouped by command text
 */
export function getAllCommandMetrics(): CommandMetricsByCommand {
  const result: CommandMetricsByCommand = {};
  
  commandMetrics.forEach(metric => {
    if (!result[metric.command]) {
      result[metric.command] = {
        success: 0,
        failure: 0
      };
    }
    
    if (metric.success) {
      result[metric.command].success += 1;
    } else {
      result[metric.command].failure += 1;
    }
    
    // Update last used timestamp
    if (!result[metric.command].lastUsed || metric.timestamp > result[metric.command].lastUsed!) {
      result[metric.command].lastUsed = metric.timestamp;
    }
  });
  
  return result;
}

/**
 * Get all recorded command metrics
 */
export function getAllMetrics() {
  return [...commandMetrics];
}

/**
 * Clear all metrics
 */
export function clearMetrics() {
  commandMetrics = [];
}

/**
 * Get the most recent commands 
 */
export function getRecentCommands(limit = 5) {
  return commandMetrics
    .slice(-limit)
    .reverse()
    .map(m => ({
      command: m.command,
      success: m.success
    }));
}
